import { DestroyRef, Injectable, InjectionToken, PendingTasks, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { catchError, defer, filter, finalize, map, of, switchMap, take, tap } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { LanguageService } from './language.service';
import { environment } from '../../../environments/environment';
import { pageFaq } from '../data/page-faq';
import {
  LocalizedFaqEntry,
  LocalizedSeo,
  OG_LOCALES,
  SeoData,
  SeoPageContext,
  brandSeoTitle,
  buildSeoGraph,
  localizedSeoUrl,
  plainSeoText,
  seoPageContext,
  serializeSeoGraph,
  socialImageForPage,
} from './seo-metadata';

export type { SeoData } from './seo-metadata';

export const SEO_INDEXING_ALLOWED = new InjectionToken<boolean>('SEO_INDEXING_ALLOWED', {
  providedIn: 'root',
  factory: () => environment.production,
});

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly language = inject(LanguageService);
  private readonly meta = inject(Meta);
  private readonly title = inject(Title);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly doc = inject(DOCUMENT);
  private readonly transloco = inject(TranslocoService);
  private readonly pendingTasks = inject(PendingTasks);
  private readonly destroyRef = inject(DestroyRef);
  private readonly indexingAllowed = inject(SEO_INDEXING_ALLOWED);
  private initialized = false;

  init(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map((event) => {
          let route = this.route;
          while (route.firstChild) route = route.firstChild;
          return {
            page: seoPageContext(event.urlAfterRedirects, this.language.currentLang()),
            seo: (route.snapshot.data['seo'] as SeoData | undefined) ?? {
              title: 'Ancelade',
              description: '',
              noindex: true,
            },
          };
        }),
        switchMap(({ page, seo }) =>
          defer(() => {
            // SSR must wait for metadata as well as for the visible translated page.
            const completeTask = this.pendingTasks.add();
            return defer(() => this.transloco.load(page.language)).pipe(
              take(1),
              map(() => true),
              catchError(() => of(false)),
              tap((translationsLoaded) => this.update(seo, page, translationsLoaded)),
              finalize(completeTask)
            );
          })
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private update(seo: SeoData, page: SeoPageContext, translationsLoaded: boolean): void {
    // A page kept out of the sitemap still has its own title, but no canonical, alternates or graph.
    const indexable = page.indexable && !seo.noindex;
    const text = this.localizedMetadata(seo, page, page.publicPage, translationsLoaded);
    const image = socialImageForPage(page);
    this.doc.documentElement.lang = page.language;
    this.title.setTitle(text.title);
    this.setMeta('name', 'description', text.description);
    this.setMeta('name', 'robots', this.robotsDirective(indexable && translationsLoaded));

    this.updateCanonical(indexable ? page.canonical : null);
    this.updateAlternates(indexable ? page : null);
    const properties: Record<string, string> = {
      'og:type': seo.ogType ?? 'website',
      'og:site_name': 'Ancelade',
      'og:locale': OG_LOCALES[page.language],
      'og:title': text.title,
      'og:description': text.description,
      'og:image': image.url,
      'og:image:secure_url': image.url,
      'og:image:type': 'image/png',
      'og:image:width': String(image.width),
      'og:image:height': String(image.height),
      'og:image:alt': text.imageAlt,
    };
    for (const [property, content] of Object.entries(properties)) this.setMeta('property', property, content);
    if (indexable) this.setMeta('property', 'og:url', page.canonical);
    else this.removeMeta('property', 'og:url');
    this.setMeta('name', 'twitter:card', 'summary_large_image');
    this.setMeta('name', 'twitter:title', text.title);
    this.setMeta('name', 'twitter:description', text.description);
    this.setMeta('name', 'twitter:image', image.url);
    this.setMeta('name', 'twitter:image:alt', text.imageAlt);

    this.doc.querySelectorAll('script[data-seo-page]').forEach((element) => element.remove());
    if (indexable) {
      const script = this.doc.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo-page', ''); // NOSONAR: Angular SSR's bundled Domino has no HTMLElement.dataset
      script.setAttribute('id', 'seo-graph');
      const faq = translationsLoaded ? this.localizedFaq(page) : [];
      script.textContent = serializeSeoGraph(buildSeoGraph(seo, text, page, faq));
      this.doc.head.appendChild(script);
    }
  }

  private robotsDirective(indexable: boolean): string {
    if (!this.indexingAllowed) return 'noindex, nofollow';
    return indexable ? 'index, follow' : 'noindex, follow';
  }

  private localizedMetadata(
    seo: SeoData,
    page: SeoPageContext,
    publicPage: boolean,
    translationsLoaded: boolean
  ): LocalizedSeo & { imageAlt: string } {
    const prefix = `seo.pages.${publicPage ? page.path || 'home' : 'not-found'}`;
    const translated = (key: string, fallback: string): string => {
      if (!translationsLoaded) return plainSeoText(fallback);
      const value: unknown = this.transloco.translate(key, {}, page.language);
      return typeof value === 'string' && value !== key && value.trim() ? plainSeoText(value) : plainSeoText(fallback);
    };
    return {
      title: brandSeoTitle(translated(`${prefix}.title`, seo.title)),
      description: translated(`${prefix}.description`, seo.description),
      name: translated(`${prefix}.name`, seo.name ?? seo.title),
      homeName: translated('seo.pages.home.name', 'Ancelade'),
      imageAlt: brandSeoTitle(translated(socialImageForPage(page).copyKey, 'Ancelade')),
    };
  }

  /** The FAQ displayed by the page template; omitted entirely if one text is missing in this language. */
  private localizedFaq(page: SeoPageContext): LocalizedFaqEntry[] {
    const faq = pageFaq(page.path);
    if (!faq) return [];
    const translated = (key: string): string => {
      const fullKey = `${faq.prefix}.${key}`;
      const value: unknown = this.transloco.translate(fullKey, {}, page.language);
      return typeof value === 'string' && value !== fullKey ? plainSeoText(value) : '';
    };
    const entries = faq.entries.map((entry) => ({
      question: translated(entry.question),
      answer: translated(entry.answer),
    }));
    return entries.every((entry) => entry.question && entry.answer) ? entries : [];
  }

  private setMeta(attribute: 'name' | 'property', name: string, content: string): void {
    const selector = `${attribute}="${name}"`;
    this.doc.querySelectorAll(`meta[${selector}]`).forEach((element, index) => {
      if (index > 0) element.remove();
    });
    this.meta.updateTag({ [attribute]: name, content }, selector);
  }

  private removeMeta(attribute: 'name' | 'property', name: string): void {
    this.doc.querySelectorAll(`meta[${attribute}="${name}"]`).forEach((element) => element.remove());
  }

  private updateAlternates(page: SeoPageContext | null): void {
    this.doc.querySelectorAll('link[rel="alternate"][hreflang]').forEach((element) => element.remove());
    this.removeMeta('property', 'og:locale:alternate');
    if (!page) return;
    for (const language of page.languages) {
      this.appendAlternate(language, localizedSeoUrl(language, page.path));
      if (language !== page.language) {
        this.meta.addTag({ property: 'og:locale:alternate', content: OG_LOCALES[language] }, true);
      }
    }
    const fallbackLanguage = page.languages.includes('fr') ? 'fr' : page.languages[0];
    this.appendAlternate(
      'x-default',
      page.path ? localizedSeoUrl(fallbackLanguage, page.path) : 'https://ancelade.cloud/'
    );
  }

  private appendAlternate(language: string, href: string): void {
    const link = this.doc.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', language);
    link.setAttribute('href', href);
    this.doc.head.appendChild(link);
  }

  private updateCanonical(url: string | null): void {
    const links = this.doc.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]');
    links.forEach((element, index) => {
      if (!url || index > 0) element.remove();
    });
    if (!url) return;
    const link = links[0] ?? this.doc.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', url);
    if (!link.parentNode) this.doc.head.appendChild(link);
  }
}
